import React, { useEffect, useState, useContext } from 'react';
import {
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon
} from '@material-ui/icons';

import * as Styled from './DocumentFileExplorer.styled';
import { FileDto } from '../Document.interface';
import { useFindFolders } from '../Document.hook';
import { FileExplorerContext } from './FileExplorer.context';
import DocumentFileExplorerItem from './DocumentFileExplorerItem';

const defaultExpandIcon = (
  <>
    <ChevronRightIcon />
    <FolderIcon />
  </>
);
const defaultCollapseIcon = (
  <>
    <ExpandMoreIcon />
    <FolderOpenIcon />
  </>
);
const defaultEndIcon = (
  <>
    <ChevronRightIcon />
    <FolderIcon />
  </>
);

const treeViewIcons = {
  defaultExpandIcon,
  defaultCollapseIcon,
  defaultEndIcon
};

// choose whether context values or state
const useSelected = (
  useContextValues: boolean = false,
  initialSelected?: string
): [string | undefined, (nodeId?: string) => void] => {
  const state = useState<string | undefined>(initialSelected);
  const ctx = useContext(FileExplorerContext);
  return useContextValues ? [ctx.selected, ctx.setSelected] : state;
};

// remove trash directory
export const filterFileTree = (
  files: FileDto[],
  hideTrashDir: boolean = false
): FileDto[] => {
  return files.filter((file: FileDto) => {
    const isTrash = file.name === 'Trash' && file.isLocked;
    return !(hideTrashDir && isTrash);
  });
};

export interface DocumentFileExplorerProps {
  files?: FileDto[];
  selected?: string;
  minHeight?: number;
  useContextValues?: boolean;
  hideRootDir?: boolean;
  hideTrashDir?: boolean;
  includeFiles?: boolean;
  onSelectFile?: (nodeId: string, file: FileDto) => void;
}

const DocumentFileExplorer: React.FC<DocumentFileExplorerProps> = (
  props: DocumentFileExplorerProps
) => {
  const {
    files: filesProp,
    selected: initialSelected,
    minHeight,
    hideRootDir,
    hideTrashDir,
    includeFiles,
    useContextValues = false,
    onSelectFile
  } = props;
  const [files, setFiles] = useState<FileDto[]>([]);
  const [selected, setSelected] = useSelected(
    useContextValues,
    initialSelected
  );
  const [expanded, setExpanded] = useState<string[]>(hideRootDir ? [] : ['-1']);
  const { setRefresh } = useContext(FileExplorerContext);
  const [{ data, isLoading, error }, get] = useFindFolders(!includeFiles);
  // set refresh callback if using context values
  useContextValues && setRefresh(get);

  useEffect(() => {
    setSelected(initialSelected);
  }, [initialSelected]);

  // if no filesProp, try to fetch files
  useEffect(() => {
    filesProp ? setFiles(filesProp) : get();
  }, [filesProp, get]);

  useEffect(() => {
    data && !filesProp && setFiles(Array.isArray(data) ? data : [data]);
  }, [data, filesProp]);

  const toggleExpand = (nodeId: string) => {
    // add to expanded if not exists
    const newExpanded = expanded.slice();
    const index = newExpanded.findIndex(id => id === nodeId);
    if (index < 0) {
      newExpanded.push(nodeId);
    } else {
      newExpanded.splice(index, 1);
    }

    setExpanded(newExpanded);
  };

  const onSelectNode = (nodeId: string, file: FileDto) => {
    // onNodeToggle doesn't call on leaf nodes, so set selected here instead
    setSelected(nodeId);
    toggleExpand(nodeId);
    onSelectFile?.(nodeId, file);
  };

  const setFileChildren = (file: FileDto, children: FileDto[]) => {
    file.children = children;
    setFiles([...files]);
  };

  // recursive render
  const renderTree = (file: FileDto, selectedNodeId?: string) => (
    <DocumentFileExplorerItem
      key={file.id}
      selected={`${file.id}` === selectedNodeId}
      renderTree={(file: FileDto) => renderTree(file, selectedNodeId)}
      {...{
        file,
        hideTrashDir,
        includeFiles,
        onSelectNode,
        setFileChildren,
        useContextValues
      }}
    />
  );

  // content to render
  let content: JSX.Element = <></>;
  if (isLoading && !files?.length) {
    content = <div>Loading...</div>;
  } else if (error) {
    content = <div>Error. Unable to retrieve files.</div>;
  } else if (!files?.length) {
    content = <div>No files to show.</div>;
  } else {
    const rootDir: FileDto = {
      id: -1,
      name: '/',
      children: files,
      isFolder: true
    };

    content = (
      <Styled.TreeView
        {...treeViewIcons}
        {...{ expanded }}
        style={{ minHeight }}
        selected={selected || ''}
      >
        {hideRootDir
          ? filterFileTree(files, hideTrashDir).map(file =>
              renderTree(file, selected)
            )
          : renderTree(rootDir, selected)}
      </Styled.TreeView>
    );
  }

  return content;
};

export default DocumentFileExplorer;
